/**
 * SlidePreview Component
 * Preview component showing the selected slide thumbnail
 * Features:
 * - Large thumbnail display
 * - Click to view full size
 * - Slide info overlay
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Presentation,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'

// ============================================
// Types
// ============================================

export interface SlidePreviewProps {
  thumbnailUrl?: string
  slideIndex: number
  slideCount?: number
  presentationName?: string
  onSlideChange?: (index: number) => void
  disabled?: boolean
  className?: string
}

// ============================================
// SlidePreview Component
// ============================================

export function SlidePreview({
  thumbnailUrl,
  slideIndex,
  slideCount = 0,
  presentationName = 'Presentation',
  onSlideChange,
  disabled = false,
  className,
}: SlidePreviewProps) {
  const [isFullScreen, setIsFullScreen] = useState(false)

  // Navigation handlers
  const canGoPrev = slideIndex > 0
  const canGoNext = slideCount > 0 && slideIndex < slideCount - 1

  const handlePrev = () => {
    if (canGoPrev && onSlideChange) {
      onSlideChange(slideIndex - 1)
    }
  }

  const handleNext = () => {
    if (canGoNext && onSlideChange) {
      onSlideChange(slideIndex + 1)
    }
  }

  return (
    <>
      {/* Preview Card */}
      <div
        className={cn(
          'relative rounded-xl overflow-hidden',
          'border border-neutral-200 dark:border-neutral-700',
          'bg-neutral-100 dark:bg-neutral-800',
          className
        )}
      >
        {/* Slide Display */}
        <div className="aspect-video relative">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={`Slide ${slideIndex + 1} of ${presentationName}`}
              className="w-full h-full object-contain bg-white"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
              <div className="text-center">
                <Presentation className="h-16 w-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  Slide {slideIndex + 1}
                </p>
              </div>
            </div>
          )}

          {/* Navigation Arrows (if navigation is enabled) */}
          {onSlideChange && slideCount > 1 && !disabled && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handlePrev}
                disabled={!canGoPrev}
                className={cn(
                  'absolute left-2 top-1/2 -translate-y-1/2',
                  'h-10 w-10 rounded-full',
                  'bg-black/40 hover:bg-black/60 text-white',
                  'disabled:opacity-30'
                )}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleNext}
                disabled={!canGoNext}
                className={cn(
                  'absolute right-2 top-1/2 -translate-y-1/2',
                  'h-10 w-10 rounded-full',
                  'bg-black/40 hover:bg-black/60 text-white',
                  'disabled:opacity-30'
                )}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}

          {/* Fullscreen Button */}
          {thumbnailUrl && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setIsFullScreen(true)}
              className={cn(
                'absolute top-2 right-2',
                'h-8 w-8 rounded-lg',
                'bg-black/40 hover:bg-black/60 text-white'
              )}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}

          {/* Slide Number Badge */}
          {slideCount > 0 && (
            <div className="absolute bottom-2 left-2 bg-black/60 text-white text-sm px-2.5 py-1 rounded-lg">
              {slideIndex + 1} / {slideCount}
            </div>
          )}
        </div>

        {/* Info Bar */}
        <div className="px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Presentation className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium text-neutral-900 dark:text-white truncate max-w-[200px]">
                {presentationName}
              </span>
            </div>
            {slideCount > 0 && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                Slide {slideIndex + 1}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Dialog */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 rounded-2xl overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Presentation className="h-5 w-5 text-orange-500" />
                {presentationName} - Slide {slideIndex + 1}
              </DialogTitle>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setIsFullScreen(false)}
                className="h-8 w-8 rounded-lg"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="relative bg-neutral-900">
            {/* Main Slide */}
            <div className="aspect-video max-h-[70vh] flex items-center justify-center">
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={`Slide ${slideIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <Presentation className="h-24 w-24 text-neutral-600 mx-auto mb-4" />
                  <p className="text-neutral-400">No preview available</p>
                </div>
              )}
            </div>

            {/* Navigation in Fullscreen */}
            {onSlideChange && slideCount > 1 && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  className={cn(
                    'absolute left-4 top-1/2 -translate-y-1/2',
                    'h-12 w-12 rounded-full',
                    'bg-white/20 hover:bg-white/30 text-white',
                    'disabled:opacity-30'
                  )}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className={cn(
                    'absolute right-4 top-1/2 -translate-y-1/2',
                    'h-12 w-12 rounded-full',
                    'bg-white/20 hover:bg-white/30 text-white',
                    'disabled:opacity-30'
                  )}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
              </>
            )}

            {/* Slide Counter */}
            {slideCount > 0 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                {slideIndex + 1} / {slideCount}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SlidePreview
